import Linkify from "linkify-react";
import type { ComponentType, JSX } from "react";
import styled from "styled-components";
import { currentColors } from "../../colors";
import type { AiMessageRoleType, AiMessageType, AiThreadRatingType } from "../../types";
import AiIcon from "../icons/AiIcon";
import ProfileIcon from "../icons/navIcons/ProfileIcon";
import BulbIcon from "../icons/taskIcons/BulbIcon";
import MagnifyingGlassIcon from "../icons/taskIcons/MagnifyingGlassIcon";
import RenderMark from "../RenderMark";

interface IMessage {
    id: string;
    type: AiMessageType;
    role: AiMessageRoleType | null;
    content: string;
    toolCallId?: string | null;
    toolName?: string | null;
    toolArguments?: string | null;
    rating?: AiThreadRatingType;
    hide?: boolean;
    reasoningSummary?: string[] | null;
    contextData?: {
        avgRelevance: number | null;
        keywordQuery: string | null;
        documentNames: string[] | null;
        documentName: string | null;
    } | null;
}

interface IProps {
    messages: IMessage[];
    fmtUser?: boolean;
    actions?: ComponentType<{ message: IMessage }>;
}

type IBox = {
    $border?: string;
};

const AiBox = styled.div<IBox>`
    display: grid;
    grid-template-columns: 35px 1fr;
    gap: 0.5rem;
    border-radius: 1rem;
    padding: 0.6rem;
    border: 1px solid ${({ $border }) => $border ?? currentColors.border};
    margin-bottom: 0.5rem;

    svg {
        width: 35px;
        height: 35px;
    }
    
    > *:last-child > *:last-child {
        margin-bottom: 0;
    }
        
    @media (min-width: 600px) {
        padding: 1rem;
        margin-bottom: 1rem;
        grid-template-columns: 45px 1fr;
    
        svg {
            width: 45px;
            height: 45px;
        }
    }
        
    @media (min-width: 800px) {
        grid-template-columns: 60px 1fr;
    
        svg {
            width: 60px;
            height: 60px;
        }
    }
`;

const ContextBox = styled.div`
    padding: 0.6rem;
    border: 1px solid ${currentColors.text};
    margin-bottom: 0.5rem;
`;

function DisplayContext({ contextData }: { contextData: IMessage["contextData"] }): JSX.Element {
    if (!contextData) {
        return null;
    }
    return (
        <ContextBox>
            {contextData.keywordQuery && (
                <>
                    {contextData.keywordQuery} <br />
                </>
            )}
            {contextData.avgRelevance !== null && (
                <>
                    {contextData.avgRelevance} <br />
                </>
            )}
            {contextData.documentName && (
                <>
                    {contextData.documentName} <br />
                </>
            )}
            {contextData.documentNames?.map((name, index) => (
                <span key={index}>
                    {name}
                    <br />
                </span>
            ))}
        </ContextBox>
    );
}

function AiThread({ messages, actions: Actions, fmtUser = false }: IProps): JSX.Element {
    if (messages.length === 0) {
        return null;
    }
    return (
        <div>
            {messages.map((message) => {
                const isUser = message.role === "user";
                const isAssistant = message.role === "assistant";
                const isMessage = message.type === "message";
                const isFunction = message.type === "function_call" || message.type === "function_call_output";
                const isReasoning = message.type === "reasoning";

                const renderIcon = () => {
                    if (isMessage) {
                        return isAssistant ? <AiIcon /> : <ProfileIcon />;
                    }
                    if (isFunction) {
                        return <MagnifyingGlassIcon />;
                    }
                    return <BulbIcon />;
                };

                const renderBody = () => {
                    if (isMessage) {
                        if (isAssistant || fmtUser) {
                            return <RenderMark content={message.content || message.toolName} />;
                        }
                        return (
                            <div>
                                {message.contextData && <DisplayContext contextData={message.contextData} />}
                                <Linkify options={{ nl2br: true }} tagName="div">
                                    {message.content}
                                </Linkify>
                            </div>
                        );
                    }
                    // Non-message types
                    if (isReasoning) {
                        return (
                            <div>
                                {message.contextData && <DisplayContext contextData={message.contextData} />}
                                {message.reasoningSummary?.map((s, i) => (
                                    <RenderMark key={i} content={s} />
                                ))}
                            </div>
                        );
                    }
                    // function_call or function_call_output
                    return (
                        <div>
                            {message.contextData && <DisplayContext contextData={message.contextData} />}
                            <Linkify options={{ nl2br: true }} tagName="div">
                                {message.content || message.toolName}
                            </Linkify>
                        </div>
                    );
                };

                return (
                    <AiBox key={message.id} $border={isUser ? currentColors.border : currentColors.text}>
                        <div>
                            {renderIcon()}
                            {!!Actions && <Actions message={message} />}
                        </div>
                        {renderBody()}
                    </AiBox>
                );
            })}
        </div>
    );
}

export default AiThread;
